/*
 * 글로벌 소듀 안에 끼워 넣고 "어둡게" 일 때만 바뀌는 색 (mj부장님 2026-09-29).
 *
 * 몰 관리 화면 곳곳에 흰 바탕, 검은 글자가 값으로 박혀 있다 (고객 관리의 CRM 색 등).
 * 이 자리들을 ep('이름', 원래색) 으로 바꾸면 CSS 변수 var(--sodu-이름, 원래색) 이 된다.
 * 변수는 아래 EMBED_DARK_CSS 에서만 채운다. 그 규칙은 끼워 넣었을 때 붙는 html[data-sodu-embed] 와
 * MUI 의 어두운 표시(html[data-mui-color-scheme="dark"])가 둘 다 있을 때만 먹는다.
 *
 * 그래서 studio.sodu.pro 를 그냥 열면 변수가 비어 있어 원래색이 그대로 나온다. 밝게일 때도 같다.
 * 색 계산(alpha 등)이나 끝에 투명도 글자를 붙이는 자리에는 쓰지 않는다. 변수 문자열이라 계산이 안 된다.
 */

/*
 * 어두운 색은 글로벌 소듀 어둡게와 같은 남색 계열이다 (flowing-sodu-link 의 globals.css :root[data-theme="dark"]).
 *   바탕 #0e1120, 카드 #171b2e, 옅은 칸 #1f2439, 선 #2a2f48, 글자 #edeff7, 보조 글자 #9aa0bd
 */
const DARK: Record<string, string> = {
    // 고객 관리 CRM 색 (customerUiHelpers.tsx)
    'crm-bg': '#171b2e',
    'crm-app': '#0e1120',
    'crm-surface': '#1f2439',
    'crm-surface2': '#232a4a',
    'crm-border': '#2a2f48',
    'crm-border-strong': '#3a4060',
    'crm-text': '#edeff7',
    'crm-text2': '#9aa0bd',
    'crm-text3': '#6f7596',
    'crm-accent-weak': '#3a1e2c',
    'crm-avatar-bg': '#2a2f48',
    'crm-avatar-text': '#c3c8e0',
    'crm-green-weak': '#143530',
    'crm-red-weak': '#3a1e24',
    // 그 밖의 밝은 칸
    paper: '#171b2e',
    soft: '#1f2439',
    'soft-hover': '#232a4a',
    'note-warn': '#3a2f14',
    'note-yellow': '#37331c',
    'note-red': '#3a1e24',
    'note-red-border': '#6b2f35',
    line: '#2a2f48',
};

/*
 * MUI 어두운 테마의 기본 색도 같은 남색으로 덮는다. 이 저장소 테마는 변수 앞머리가 --v- 다 (defaultTheme.tsx 의 cssVarPrefix). MUI 는 어두울 때 카드에 밝은 막(overlays)을 씌워
 * 회색으로 떠 보이게 하므로 그것도 뺀다.
 */
const MUI_DARK: Record<string, string> = {
    '--v-palette-background-default': '#0e1120',
    '--v-palette-background-defaultChannel': '14 17 32',
    '--v-palette-background-paper': '#171b2e',
    '--v-palette-background-paperChannel': '23 27 46',
    '--v-palette-divider': '#2a2f48',
    '--v-palette-text-primary': '#edeff7',
    '--v-palette-text-primaryChannel': '237 239 247',
    '--v-palette-text-secondary': '#9aa0bd',
    '--v-palette-text-secondaryChannel': '154 160 189',
    ...Object.fromEntries(Array.from({ length: 25 }, (_, i) => [`--v-overlays-${i}`, 'none'])),
};

export function ep(name: keyof typeof DARK & string, light: string): string {
    return `var(--sodu-${name}, ${light})`;
}

/*
 * 스크롤바도 글로벌 소듀와 같은 얇은 막대로 맞춘다 (mj부장님 2026-09-29). 끼워 넣었을 때만 먹는다.
 * 어두우면 color-scheme 을 dark 로 두어 브라우저 기본 칸(입력칸 달력 등)도 어둡게 그린다.
 */
const SCROLLBAR_CSS = [
    'html[data-sodu-embed] *{scrollbar-width:thin;scrollbar-color:rgba(128,134,150,.45) transparent}',
    'html[data-sodu-embed] ::-webkit-scrollbar{width:8px;height:8px}',
    'html[data-sodu-embed] ::-webkit-scrollbar-track{background:transparent}',
    'html[data-sodu-embed] ::-webkit-scrollbar-thumb{background:rgba(128,134,150,.45);border-radius:8px;border:2px solid transparent;background-clip:padding-box}',
    'html[data-sodu-embed] ::-webkit-scrollbar-thumb:hover{background:rgba(128,134,150,.7);background-clip:padding-box}',
    'html[data-sodu-embed][data-mui-color-scheme="dark"]{color-scheme:dark}',
].join('');

const DARK_VARS = [
    ...Object.entries(DARK).map(([k, v]) => `--sodu-${k}:${v};`),
    ...Object.entries(MUI_DARK).map(([k, v]) => `${k}:${v};`),
].join('');

export const EMBED_DARK_CSS = `html[data-sodu-embed][data-mui-color-scheme="dark"]{${DARK_VARS}}${SCROLLBAR_CSS}`;
